import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import CloudflareTurnstile from '../components/CloudflareTurnstile';

import SEO from '../components/SEO';
import '../styles/Auth.css';

const SignupPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const referralCode = searchParams.get('ref') || '';
  const { signUp, signOut, isAuthenticated, loading: authLoading, error: authError, clearError } = useAuth();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(30);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const cooldownRef = useRef(null);

  useEffect(() => {
    if (success && resendCooldown > 0) {
      cooldownRef.current = setInterval(() => {
        setResendCooldown(prev => {
          if (prev <= 1) {
            clearInterval(cooldownRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(cooldownRef.current);
  }, [success, resendCooldown > 0]);

  const handleResendEmail = async () => {
    if (resendCooldown > 0 || resendLoading) return;

    setResendLoading(true);
    setResendMessage('');

    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: formData.email,
      });

      if (error) {
        setResendMessage('Failed to resend. Please try again later.');
      } else {
        setResendMessage('Confirmation email sent!');
        setResendCooldown(30);
      }
    } catch (err) {
      setResendMessage('Failed to resend. Please try again later.');
    } finally {
      setResendLoading(false);
    }
  };

  useEffect(() => {
    if (!isAuthenticated) {
      signOut();
    }
  }, []);

  useEffect(() => {
    return () => clearError();
  }, [clearError]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleTurnstileVerify = (token) => {
    setTurnstileToken(token);
    setError('');
  };

  const handleTurnstileError = () => {
    setTurnstileToken(null);
    setError('Verification failed. Please try again.');
  };

  const handleTurnstileExpire = () => {
    setTurnstileToken(null);
  };

  const validateForm = () => {
    if (!formData.email || !formData.password || !formData.confirmPassword) {
      setError('Please fill in all fields');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return false;
    }

    if (!/[A-Z]/.test(formData.password)) {
      setError('Password must contain at least one uppercase letter');
      return false;
    }
    if (!/[a-z]/.test(formData.password)) {
      setError('Password must contain at least one lowercase letter');
      return false;
    }
    if (!/[0-9]/.test(formData.password)) {
      setError('Password must contain at least one number');
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }

    if (!turnstileToken) {
      setError('Please complete the verification');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    setError('');

    try {
      const result = await signUp(formData.email, formData.password, turnstileToken, referralCode || null);

      if (result.success) {
        if (result.data?.user?.identities?.length === 0) {
          setError('An account with this email already exists. Please go to Login.');
          setTurnstileToken(null);
          setTurnstileResetKey(prev => prev + 1);
        } else {
          setSuccess(true);
        }
      } else {
        setError(result.error || 'Signup failed. Please try again.');
        setTurnstileToken(null);
        setTurnstileResetKey(prev => prev + 1);
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
      setTurnstileToken(null);
      setTurnstileResetKey(prev => prev + 1);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="auth-page">
        <div className="auth-left">
          <div className="auth-logo">
            <Link to="/" className="auth-logo-link">
              <img src="/pathproxy.png" alt="PathProxy Logo" className="auth-logo-image" />
              <span className="brand-name">pathproxy</span>
            </Link>
          </div>

          <div className="auth-form-container">
            <div className="success-message">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#E8FF00" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                <polyline points="22,4 12,14.01 9,11.01"/>
              </svg>
              <h1>Check Your Email</h1>
              <p>We've sent a confirmation link to <strong>{formData.email}</strong></p>
              <p className="auth-subtitle">Please check your inbox and click the link to activate your account.</p>
              <div className="spam-warning">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                  <line x1="12" y1="9" x2="12" y2="13"/>
                  <line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
                <span>Can't find the email? Please check your <strong>Spam</strong> or <strong>Junk</strong> folder.</span>
              </div>

              {resendMessage && (
                <div className={`resend-message ${resendMessage.includes('sent') ? 'success' : 'error'}`}>
                  {resendMessage}
                </div>
              )}

              <button
                className="resend-btn"
                onClick={handleResendEmail}
                disabled={resendCooldown > 0 || resendLoading}
              >
                {resendLoading ? (
                  <>
                    <span className="btn-spinner"></span>
                    Sending...
                  </>
                ) : resendCooldown > 0 ? (
                  `Resend email (${resendCooldown}s)`
                ) : (
                  'Resend confirmation email'
                )}
              </button>

              <Link to="/login" className="auth-submit-btn" style={{ display: 'inline-block', textDecoration: 'none', marginTop: '12px' }}>
                Go to Login
              </Link>
            </div>
          </div>
        </div>

        <div className="auth-right">
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <SEO title="Sign Up" description="Create a PathProxy account. Get access to 40M+ residential proxies in 150+ countries. No KYC required. Crypto payments accepted." path="/signup" noindex={true} />
      <div className="auth-left">
        <div className="auth-logo">
          <Link to="/" className="auth-logo-link">
            <img src="/pathproxy.png" alt="PathProxy Logo" className="auth-logo-image" />
            <span className="brand-name">pathproxy</span>
          </Link>
        </div>

        <div className="auth-form-container">
          <h1>Create Account</h1>
          <p className="auth-subtitle">Enter your details to create your account</p>

          {referralCode ? (
            <div className="referral-banner">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
              </svg>
              <span>You were referred! You may receive <strong>bonus bandwidth</strong> after verification.</span>
            </div>
          ) : (
            <div className="referral-banner">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
              </svg>
              <span>Have a referral code? Refer friends and earn <strong>bonus bandwidth</strong>!</span>
            </div>
          )}

          <p className="auth-switch">
            Already have an account? <Link to="/login">Login</Link>
          </p>

          {(error || authError) && (
            <div className="auth-error">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="15" y1="9" x2="9" y2="15"/>
                <line x1="9" y1="9" x2="15" y2="15"/>
              </svg>
              <span>
                {error || authError}
                {(error || authError || '').includes('already exists') && (
                  <> <Link to="/login" style={{ color: '#E8FF00' }}>Go to Login</Link></>
                )}
              </span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="auth-form">
            <div className="form-group">
              <label>Email Address <span className="required">*</span></label>
              <div className="input-with-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter your email address"
                  required
                  autoComplete="email"
                  maxLength={254}
                  disabled={loading || authLoading}
                />
              </div>
            </div>

            <div className="form-group">
              <label>Password <span className="required">*</span></label>
              <div className="input-with-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Min 8 chars, uppercase, lowercase, number"
                  required
                  autoComplete="new-password"
                  minLength={8}
                  maxLength={128}
                  disabled={loading || authLoading}
                />
              </div>
            </div>

            <div className="form-group">
              <label>Confirm Password <span className="required">*</span></label>
              <div className="input-with-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
                <input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Confirm your password"
                  required
                  autoComplete="new-password"
                  maxLength={128}
                  disabled={loading || authLoading}
                />
              </div>
            </div>

            <div className="captcha-box">
              <CloudflareTurnstile
                onVerify={handleTurnstileVerify}
                onError={handleTurnstileError}
                onExpire={handleTurnstileExpire}
                theme="dark"
                resetKey={turnstileResetKey}
              />
            </div>

            <button
              type="submit"
              className="auth-submit-btn"
              disabled={loading || authLoading || !turnstileToken}
            >
              {loading || authLoading ? (
                <>
                  <span className="btn-spinner"></span>
                  Creating account...
                </>
              ) : (
                'Sign Up'
              )}
            </button>

            <p className="terms-text">
              By signing up, you agree to our <Link to="/terms">Terms of Service</Link> and <Link to="/privacy">Privacy Policy</Link>
            </p>
          </form>
        </div>
      </div>

      <div className="auth-right">
      </div>
    </div>
  );
};

export default SignupPage;
